import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { Modal, Field, Th, Td } from "./Customers";
import { Plus, Calculator, Check, X, Clock, UserPlus, UserMinus, Star, Briefcase } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function HR() {
  const { tenant } = useAuth();
  const [tab, setTab] = useState("employees");
  const [emps, setEmps] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [runs, setRuns] = useState([]);
  const [newEmp, setNewEmp] = useState(false);
  const [newLeave, setNewLeave] = useState(false);
  const [runOpen, setRunOpen] = useState(false);
  const [viewRun, setViewRun] = useState(null);

  const load = () => Promise.all([
    api.get("/hr/employees").then(({ data }) => setEmps(data)),
    api.get("/hr/leave").then(({ data }) => setLeaves(data)),
    api.get("/hr/payroll").then(({ data }) => setRuns(data)),
  ]);
  useEffect(() => { load(); }, []);

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="hr-page">
      <PageHeader
        title="HR & Payroll"
        subtitle="Employees, leave, payroll, attendance, onboarding/offboarding, appraisals & recruitment."
        action={
          <div className="flex gap-2">
            {tab === "employees" && <button onClick={() => setNewEmp(true)} data-testid="new-employee-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm rounded-sm"><Plus size={14} /> New employee</button>}
            {tab === "leave" && <button onClick={() => setNewLeave(true)} data-testid="new-leave-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm rounded-sm"><Plus size={14} /> New leave request</button>}
            {tab === "payroll" && <button onClick={() => setRunOpen(true)} data-testid="run-payroll-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm rounded-sm"><Calculator size={14} /> Run payroll</button>}
          </div>
        }
      />

      <div className="mt-6 flex flex-wrap gap-1 border-b border-[#DEE2E6]">
        <Tab v="employees" cur={tab} onClick={() => setTab("employees")}>Employees</Tab>
        <Tab v="leave" cur={tab} onClick={() => setTab("leave")}>Leave</Tab>
        <Tab v="payroll" cur={tab} onClick={() => setTab("payroll")}>Payroll runs</Tab>
        <Tab v="attendance" cur={tab} onClick={() => setTab("attendance")}>Attendance</Tab>
        <Tab v="onboarding" cur={tab} onClick={() => setTab("onboarding")}>Onboarding</Tab>
        <Tab v="offboarding" cur={tab} onClick={() => setTab("offboarding")}>Offboarding</Tab>
        <Tab v="appraisals" cur={tab} onClick={() => setTab("appraisals")}>Appraisals</Tab>
        <Tab v="recruitment" cur={tab} onClick={() => setTab("recruitment")}>Recruitment</Tab>
      </div>

      {tab === "employees" && (
        <div className="mt-6 border border-[#0A0A0A] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
              <tr><Th>No.</Th><Th>Name</Th><Th>Department</Th><Th>Title</Th><Th align="right">Base salary</Th><Th align="right">Leave bal.</Th></tr>
            </thead>
            <tbody>
              {emps.map((e, i) => (
                <tr key={e.id} data-testid={`employee-row-${i}`} className="border-b border-[#DEE2E6] hover:bg-[#F8F9FA]">
                  <Td className="font-mono text-xs">{e.employee_no}</Td>
                  <Td><div className="font-medium">{e.name}</div><div className="text-[#495057] text-xs">{e.email}</div></Td>
                  <Td>{e.department}</Td><Td className="text-[#495057]">{e.title}</Td>
                  <Td align="right" className="tabular-nums">{tenant?.currency || ""} {e.base_salary.toLocaleString()}</Td>
                  <Td align="right" className="tabular-nums">{e.leave_balance} d</Td>
                </tr>
              ))}
              {emps.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#495057]">No employees yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "leave" && (
        <div className="mt-6 border border-[#0A0A0A] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
              <tr><Th>Employee</Th><Th>Dates</Th><Th align="right">Days</Th><Th>Reason</Th><Th>Status</Th><Th></Th></tr>
            </thead>
            <tbody>
              {leaves.map((lr, i) => (
                <tr key={lr.id} data-testid={`leave-row-${i}`} className="border-b border-[#DEE2E6] hover:bg-[#F8F9FA]">
                  <Td>{lr.employee_name}</Td>
                  <Td className="text-[#495057] text-xs">{lr.start_date} → {lr.end_date}</Td>
                  <Td align="right" className="tabular-nums">{lr.days}</Td>
                  <Td className="text-[#495057] truncate max-w-[300px]">{lr.reason}</Td>
                  <Td><LeaveStatus s={lr.status} /></Td>
                  <Td>
                    {lr.status === "pending" && (
                      <div className="flex gap-1">
                        <button onClick={async () => { await api.post(`/hr/leave/${lr.id}/approve`); load(); }} data-testid={`leave-approve-${i}`} className="px-2 py-1 text-xs bg-[#0B7A75] text-white inline-flex items-center gap-1"><Check size={10} /> Approve</button>
                        <button onClick={async () => { await api.post(`/hr/leave/${lr.id}/reject`); load(); }} data-testid={`leave-reject-${i}`} className="px-2 py-1 text-xs border border-[#D90429] text-[#D90429] inline-flex items-center gap-1"><X size={10} /> Reject</button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
              {leaves.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#495057]">No leave requests yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "payroll" && (
        <div className="mt-6 border border-[#0A0A0A] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
              <tr><Th>Number</Th><Th>Period</Th><Th align="right">Gross</Th><Th align="right">SSNIT (5.5%)</Th><Th align="right">PAYE</Th><Th align="right">Net</Th><Th align="right">Employer SSNIT (13%)</Th></tr>
            </thead>
            <tbody>
              {runs.map((r, i) => (
                <tr key={r.id} data-testid={`payroll-row-${i}`} onClick={() => setViewRun(r)} className="border-b border-[#DEE2E6] hover:bg-[#F8F9FA] cursor-pointer">
                  <Td className="font-mono text-xs">{r.number}</Td><Td>{r.period}</Td>
                  <Td align="right" className="tabular-nums">{r.total_gross.toLocaleString()}</Td>
                  <Td align="right" className="tabular-nums text-[#D90429]">{r.total_ssnit_employee.toLocaleString()}</Td>
                  <Td align="right" className="tabular-nums text-[#D90429]">{r.total_paye.toLocaleString()}</Td>
                  <Td align="right" className="tabular-nums font-bold text-[#0B7A75]">{r.total_net.toLocaleString()}</Td>
                  <Td align="right" className="tabular-nums text-[#495057]">{r.total_ssnit_employer.toLocaleString()}</Td>
                </tr>
              ))}
              {runs.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#495057]">No payroll runs yet. Click &ldquo;Run payroll&rdquo; to generate one.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "attendance" && <AttendanceTab employees={emps} />}
      {tab === "onboarding" && <OnboardingTab employees={emps} />}
      {tab === "offboarding" && <OffboardingTab employees={emps} />}
      {tab === "appraisals" && <AppraisalsTab employees={emps} />}
      {tab === "recruitment" && <RecruitmentTab />}

      {newEmp && <EmpForm onClose={() => setNewEmp(false)} onSaved={() => { setNewEmp(false); load(); }} />}
      {newLeave && <LeaveForm employees={emps} onClose={() => setNewLeave(false)} onSaved={() => { setNewLeave(false); load(); }} />}
      {runOpen && <RunForm onClose={() => setRunOpen(false)} onSaved={() => { setRunOpen(false); load(); }} />}
      {viewRun && <PayrollDetail run={viewRun} onClose={() => setViewRun(null)} />}
    </div>
  );
}

function Tab({ v, cur, onClick, children }) {
  return (
    <button onClick={onClick} data-testid={`hr-tab-${v}`}
      className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${cur === v ? "border-[#FF4F00] text-[#0A0A0A]" : "border-transparent text-[#495057] hover:text-[#0A0A0A]"}`}>
      {children}
    </button>
  );
}

function LeaveStatus({ s }) {
  const cls = { pending: "border-[#F5B700] bg-[#F5B700]/10 text-[#0A0A0A]", approved: "border-[#0B7A75] bg-[#0B7A75]/10 text-[#0B7A75]", rejected: "border-[#D90429] bg-[#D90429]/10 text-[#D90429]" }[s] || "";
  return <span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5 ${cls}`} data-testid={`leave-status-${s}`}>{s}</span>;
}

function EmpForm({ onClose, onSaved }) {
  const [f, setF] = useState({ employee_no: "", name: "", email: "", phone: "", department: "General", title: "Staff", base_salary: 0, hire_date: "", leave_balance: 20 });
  const [err, setErr] = useState("");
  const save = async (e) => { e.preventDefault();
    try { await api.post("/hr/employees", { ...f, base_salary: parseFloat(f.base_salary || 0), leave_balance: parseFloat(f.leave_balance || 0) }); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title="New employee" onClose={onClose} testid="employee-modal" wide>
      <form onSubmit={save} className="grid grid-cols-2 gap-4" data-testid="employee-form">
        <Field label="Employee no." required value={f.employee_no} onChange={(v) => setF({ ...f, employee_no: v })} testid="employee-no" />
        <Field label="Name" required value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="employee-name" />
        <Field label="Email" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="employee-email" />
        <Field label="Phone" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="employee-phone" />
        <Field label="Department" value={f.department} onChange={(v) => setF({ ...f, department: v })} testid="employee-dept" />
        <Field label="Title" value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="employee-title" />
        <Field label="Base salary (GHS)" type="number" value={f.base_salary} onChange={(v) => setF({ ...f, base_salary: v })} testid="employee-salary" />
        <Field label="Leave balance (days)" type="number" value={f.leave_balance} onChange={(v) => setF({ ...f, leave_balance: v })} testid="employee-leave" />
        <div className="col-span-2"><Field label="Hire date" value={f.hire_date} onChange={(v) => setF({ ...f, hire_date: v })} testid="employee-hire" /></div>
        {err && <div className="col-span-2 text-sm text-[#D90429]">{err}</div>}
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" data-testid="employee-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Save</button>
        </div>
      </form>
    </Modal>
  );
}

function LeaveForm({ employees, onClose, onSaved }) {
  const [f, setF] = useState({ employee_id: "", start_date: "", end_date: "", days: 1, reason: "" });
  const [err, setErr] = useState("");
  const save = async (e) => { e.preventDefault();
    try { await api.post("/hr/leave", { ...f, days: parseFloat(f.days) }); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title="New leave request" onClose={onClose} testid="leave-modal">
      <form onSubmit={save} className="space-y-4" data-testid="leave-form">
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Employee</span>
          <select required value={f.employee_id} onChange={(e) => setF({ ...f, employee_id: e.target.value })} data-testid="leave-emp"
            className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            <option value="">— select —</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.employee_no} · {e.name} ({e.leave_balance}d bal.)</option>)}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start date" value={f.start_date} onChange={(v) => setF({ ...f, start_date: v })} testid="leave-start" required />
          <Field label="End date" value={f.end_date} onChange={(v) => setF({ ...f, end_date: v })} testid="leave-end" required />
        </div>
        <Field label="Days" type="number" required value={f.days} onChange={(v) => setF({ ...f, days: v })} testid="leave-days" />
        <Field label="Reason" required value={f.reason} onChange={(v) => setF({ ...f, reason: v })} testid="leave-reason" />
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" data-testid="leave-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Submit</button>
        </div>
      </form>
    </Modal>
  );
}

function RunForm({ onClose, onSaved }) {
  const today = new Date();
  const [period, setPeriod] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
  const [err, setErr] = useState("");
  const save = async (e) => { e.preventDefault();
    try { await api.post("/hr/payroll", { period }); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Modal title="Run payroll" onClose={onClose} testid="payroll-run-modal">
      <form onSubmit={save} className="space-y-4" data-testid="payroll-run-form">
        <Field label="Period (YYYY-MM)" required value={period} onChange={setPeriod} testid="payroll-period" />
        <div className="text-xs text-[#495057]">Applies Ghana statutory: SSNIT 5.5% employee + 13% employer, PAYE bands 2024/25.</div>
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" data-testid="payroll-run-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Generate</button>
        </div>
      </form>
    </Modal>
  );
}

function PayrollDetail({ run, onClose }) {
  const { tenant } = useAuth();
  return (
    <Modal title={`${run.number} · ${run.period}`} onClose={onClose} testid="payroll-detail-modal" wide>
      <div className="overflow-x-auto" data-testid="payroll-detail">
        <table className="w-full text-sm border border-[#DEE2E6]">
          <thead className="bg-[#F8F9FA] text-[10px] uppercase tracking-[0.15em] text-[#495057]">
            <tr><Th>No.</Th><Th>Name</Th><Th align="right">Gross</Th><Th align="right">SSNIT (5.5%)</Th><Th align="right">PAYE</Th><Th align="right">Net</Th><Th align="right">SSNIT Emp (13%)</Th></tr>
          </thead>
          <tbody>
            {run.lines.map((l, i) => (
              <tr key={i} className="border-t border-[#DEE2E6]" data-testid={`payslip-${i}`}>
                <Td className="font-mono text-xs">{l.employee_no}</Td><Td>{l.name}</Td>
                <Td align="right" className="tabular-nums">{l.gross_pay.toLocaleString()}</Td>
                <Td align="right" className="tabular-nums text-[#D90429]">{l.ssnit_employee.toLocaleString()}</Td>
                <Td align="right" className="tabular-nums text-[#D90429]">{l.paye.toLocaleString()}</Td>
                <Td align="right" className="tabular-nums font-bold text-[#0B7A75]">{l.net_pay.toLocaleString()}</Td>
                <Td align="right" className="tabular-nums text-[#495057]">{l.ssnit_employer.toLocaleString()}</Td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-[#F8F9FA] font-bold">
            <tr>
              <Td colSpan={2} className="text-xs uppercase tracking-[0.15em]">Totals</Td>
              <Td align="right" className="tabular-nums">{run.total_gross.toLocaleString()}</Td>
              <Td align="right" className="tabular-nums">{run.total_ssnit_employee.toLocaleString()}</Td>
              <Td align="right" className="tabular-nums">{run.total_paye.toLocaleString()}</Td>
              <Td align="right" className="tabular-nums text-[#0B7A75]">{tenant?.currency || ""} {run.total_net.toLocaleString()}</Td>
              <Td align="right" className="tabular-nums">{run.total_ssnit_employer.toLocaleString()}</Td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Modal>
  );
}


/* =============================================================
 *  ATTENDANCE
 * ============================================================= */
function AttendanceTab({ employees }) {
  const [rows, setRows] = useState([]);
  const [empId, setEmpId] = useState("");
  const load = () => api.get("/hr/attendance").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const clockIn = async () => { if (!empId) return; try { await api.post("/hr/attendance/clock-in", { employee_id: empId, method: "manual" }); load(); } catch (e) { alert(e?.response?.data?.detail || "Failed"); } };
  const clockOut = async () => { if (!empId) return; try { await api.post("/hr/attendance/clock-out", { employee_id: empId }); load(); } catch (e) { alert(e?.response?.data?.detail || "Failed"); } };
  return (
    <div className="mt-6">
      <div className="flex items-end gap-3 mb-4">
        <label className="block min-w-[260px]">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Employee</span>
          <select value={empId} onChange={(e) => setEmpId(e.target.value)} data-testid="att-emp-select" className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            <option value="">— select —</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.employee_no} · {e.name}</option>)}
          </select>
        </label>
        <button onClick={clockIn} disabled={!empId} data-testid="clock-in-btn" className="inline-flex items-center gap-2 bg-[#0B7A75] text-white px-4 py-2.5 text-sm disabled:opacity-50"><Clock size={14}/> Clock in</button>
        <button onClick={clockOut} disabled={!empId} data-testid="clock-out-btn" className="inline-flex items-center gap-2 border border-[#0A0A0A] px-4 py-2.5 text-sm disabled:opacity-50"><Clock size={14}/> Clock out</button>
      </div>
      <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="attendance-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
            <tr><Th>Date</Th><Th>Employee</Th><Th>Clock in</Th><Th>Clock out</Th><Th>Method</Th><Th align="right">Hours</Th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="border-b border-[#DEE2E6]" data-testid={`attendance-row-${i}`}>
                <Td>{r.date}</Td>
                <Td>{r.employee_name}</Td>
                <Td className="text-xs text-[#495057]">{new Date(r.clock_in).toLocaleTimeString()}</Td>
                <Td className="text-xs text-[#495057]">{r.clock_out ? new Date(r.clock_out).toLocaleTimeString() : <span className="text-[#FF4F00]">OPEN</span>}</Td>
                <Td className="text-xs uppercase tracking-[0.15em] text-[#495057]">{r.method}</Td>
                <Td align="right" className="tabular-nums font-bold">{r.hours}</Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#495057]">No attendance records yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =============================================================
 *  ONBOARDING
 * ============================================================= */
function OnboardingTab({ employees }) {
  const [rows, setRows] = useState([]);
  const [empId, setEmpId] = useState("");
  const load = () => api.get("/hr/onboarding").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const start = async () => { try { await api.post("/hr/onboarding/start", { employee_id: empId }); setEmpId(""); load(); } catch (e) { alert(e?.response?.data?.detail || "Failed"); } };
  const completeTask = async (oid, idx) => { await api.post(`/hr/onboarding/${oid}/complete-task`, { task_index: idx }); load(); };
  return (
    <div className="mt-6">
      <div className="flex items-end gap-3 mb-4">
        <label className="block min-w-[260px]">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Start onboarding for</span>
          <select value={empId} onChange={(e) => setEmpId(e.target.value)} data-testid="ob-emp-select" className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            <option value="">— select —</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.employee_no} · {e.name}</option>)}
          </select>
        </label>
        <button onClick={start} disabled={!empId} data-testid="start-onboarding-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm disabled:opacity-50"><UserPlus size={14}/> Start onboarding</button>
      </div>
      <div className="space-y-4" data-testid="onboarding-list">
        {rows.map((ob) => (
          <div key={ob.id} className="border border-[#0A0A0A] bg-white p-4" data-testid={`onboarding-${ob.id}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="font-bold">{ob.employee_name}</div>
                <div className="text-xs text-[#495057]">Started {new Date(ob.started_at).toLocaleDateString()} · Provisioning: {ob.provisioning?.email_account || "—"}</div>
              </div>
              <div className="text-right">
                <div className="text-xl font-bold tabular-nums">{ob.completion_pct}%</div>
                <span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5 ${ob.status === "complete" ? "border-[#0B7A75] text-[#0B7A75]" : "border-[#F5B700] text-[#F5B700]"}`}>{ob.status}</span>
              </div>
            </div>
            <div className="space-y-1">
              {ob.tasks.map((t, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-[#DEE2E6] last:border-0">
                  <div className="flex items-center gap-2"><span className={`inline-block w-4 h-4 border ${t.completed ? "bg-[#0B7A75] border-[#0B7A75]" : "border-[#0A0A0A]"} text-white text-[10px] grid place-items-center`}>{t.completed && "✓"}</span><span className={t.completed ? "line-through text-[#495057]" : ""}>{t.title}</span><span className="text-[10px] uppercase tracking-[0.15em] text-[#495057]">[{t.category}]</span></div>
                  {!t.completed && <button onClick={() => completeTask(ob.id, i)} data-testid={`ob-task-${ob.id}-${i}`} className="text-xs px-2 py-1 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white">Mark done</button>}
                </div>
              ))}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="p-8 text-center text-[#495057] border border-[#DEE2E6]">No onboarding in progress.</div>}
      </div>
    </div>
  );
}

/* =============================================================
 *  OFFBOARDING
 * ============================================================= */
function OffboardingTab({ employees }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/hr/offboarding").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const completeTask = async (oid, idx) => { await api.post(`/hr/offboarding/${oid}/complete-task`, { task_index: idx }); load(); };
  return (
    <div className="mt-6">
      <div className="flex justify-end mb-4">
        <button onClick={() => setOpen(true)} data-testid="start-offboarding-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm"><UserMinus size={14}/> Start offboarding</button>
      </div>
      <div className="space-y-4" data-testid="offboarding-list">
        {rows.map((ob) => (
          <div key={ob.id} className="border border-[#0A0A0A] bg-white p-4" data-testid={`offboarding-${ob.id}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="font-bold">{ob.employee_name}</div>
                <div className="text-xs text-[#495057]">Last day {ob.last_day} · Reason: {ob.reason}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5 ${ob.status === "complete" ? "border-[#0B7A75] text-[#0B7A75]" : "border-[#D90429] text-[#D90429]"}`}>{ob.status}</span>
            </div>
            <div className="space-y-1">
              {ob.tasks.map((t, i) => (
                <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-[#DEE2E6] last:border-0">
                  <div className="flex items-center gap-2"><span className={`inline-block w-4 h-4 border ${t.completed ? "bg-[#0B7A75] border-[#0B7A75]" : "border-[#0A0A0A]"} text-white text-[10px] grid place-items-center`}>{t.completed && "✓"}</span><span className={t.completed ? "line-through text-[#495057]" : ""}>{t.title}</span></div>
                  {!t.completed && <button onClick={() => completeTask(ob.id, i)} data-testid={`off-task-${ob.id}-${i}`} className="text-xs px-2 py-1 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white">Mark done</button>}
                </div>
              ))}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="p-8 text-center text-[#495057] border border-[#DEE2E6]">No offboarding records.</div>}
      </div>
      {open && <OffboardForm employees={employees} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function OffboardForm({ employees, onClose, onSaved }) {
  const [f, setF] = useState({ employee_id: "", last_day: "", reason: "" });
  const [err, setErr] = useState("");
  const save = async (e) => { e.preventDefault(); try { await api.post("/hr/offboarding/start", f); onSaved(); } catch (e) { setErr(e?.response?.data?.detail || "Failed"); } };
  return (
    <Modal title="Start offboarding" onClose={onClose} testid="offboard-modal">
      <form onSubmit={save} className="space-y-4" data-testid="offboard-form">
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Employee</span>
          <select required value={f.employee_id} onChange={(e) => setF({ ...f, employee_id: e.target.value })} data-testid="off-emp" className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            <option value="">— select —</option>
            {employees.filter((e) => e.status !== "terminated").map((e) => <option key={e.id} value={e.id}>{e.employee_no} · {e.name}</option>)}
          </select>
        </label>
        <Field label="Last day" required value={f.last_day} onChange={(v) => setF({ ...f, last_day: v })} testid="off-lastday" />
        <Field label="Reason" required value={f.reason} onChange={(v) => setF({ ...f, reason: v })} testid="off-reason" />
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" data-testid="off-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Start</button>
        </div>
      </form>
    </Modal>
  );
}

/* =============================================================
 *  APPRAISALS
 * ============================================================= */
function AppraisalsTab({ employees }) {
  const [cycles, setCycles] = useState([]);
  const [appraisals, setAppraisals] = useState([]);
  const [newCycle, setNewCycle] = useState(false);
  const [scoring, setScoring] = useState(null);
  const load = () => Promise.all([
    api.get("/hr/appraisal-cycles").then(({ data }) => setCycles(data)),
    api.get("/hr/appraisals").then(({ data }) => setAppraisals(data)),
  ]);
  useEffect(() => { load(); }, []);
  return (
    <div className="mt-6">
      <div className="flex justify-end gap-2 mb-4">
        <button onClick={() => setNewCycle(true)} data-testid="new-cycle-btn" className="inline-flex items-center gap-2 border border-[#0A0A0A] px-4 py-2.5 text-sm"><Plus size={14}/> New cycle</button>
        <button onClick={() => setScoring({})} disabled={cycles.length === 0} data-testid="new-appraisal-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm disabled:opacity-50"><Star size={14}/> Score employee</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-[#0A0A0A] bg-white" data-testid="cycles-table">
          <div className="bg-[#F8F9FA] px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#495057] border-b border-[#DEE2E6]">Cycles</div>
          {cycles.map((c) => (
            <div key={c.id} className="px-4 py-3 border-b border-[#DEE2E6] last:border-0 text-sm" data-testid={`cycle-${c.id}`}>
              <div className="font-medium">{c.name}</div>
              <div className="text-xs text-[#495057]">{c.period} · review due {c.review_due}</div>
            </div>
          ))}
          {cycles.length === 0 && <div className="p-8 text-center text-[#495057]">No cycles.</div>}
        </div>
        <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="appraisals-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
              <tr><Th>Employee</Th><Th>Cycle</Th><Th align="right">Goals</Th><Th align="right">Comp.</Th><Th align="right">Overall</Th><Th>Rating</Th></tr>
            </thead>
            <tbody>
              {appraisals.map((a, i) => (
                <tr key={a.id} className="border-b border-[#DEE2E6]" data-testid={`appraisal-row-${i}`}>
                  <Td>{a.employee_name}</Td>
                  <Td className="text-xs text-[#495057]">{a.cycle_name}</Td>
                  <Td align="right" className="tabular-nums">{a.goals_score}</Td>
                  <Td align="right" className="tabular-nums">{a.competency_score}</Td>
                  <Td align="right" className="tabular-nums font-bold">{a.overall_score}</Td>
                  <Td className="text-xs">{a.rating}</Td>
                </tr>
              ))}
              {appraisals.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#495057]">No appraisals.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {newCycle && <CycleForm onClose={() => setNewCycle(false)} onSaved={() => { setNewCycle(false); load(); }} />}
      {scoring && <AppraisalForm cycles={cycles} employees={employees} onClose={() => setScoring(null)} onSaved={() => { setScoring(null); load(); }} />}
    </div>
  );
}

function CycleForm({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", period: "", review_due: "" });
  const [err, setErr] = useState("");
  const save = async (e) => { e.preventDefault(); try { await api.post("/hr/appraisal-cycles", f); onSaved(); } catch (e) { setErr(e?.response?.data?.detail || "Failed"); } };
  return (
    <Modal title="New appraisal cycle" onClose={onClose} testid="cycle-modal">
      <form onSubmit={save} className="space-y-4" data-testid="cycle-form">
        <Field label="Cycle name" required value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="cy-name" />
        <Field label="Period (e.g. 2026-Q1)" required value={f.period} onChange={(v) => setF({ ...f, period: v })} testid="cy-period" />
        <Field label="Review due (YYYY-MM-DD)" required value={f.review_due} onChange={(v) => setF({ ...f, review_due: v })} testid="cy-due" />
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button><button type="submit" data-testid="cy-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Save</button></div>
      </form>
    </Modal>
  );
}

function AppraisalForm({ cycles, employees, onClose, onSaved }) {
  const [f, setF] = useState({ cycle_id: cycles[0]?.id || "", employee_id: "", goals_score: 3, competency_score: 3, overall_score: 3, strengths: "", growth_areas: "", notes: "" });
  const [err, setErr] = useState("");
  const save = async (e) => { e.preventDefault(); try { await api.post("/hr/appraisals", { ...f, goals_score: parseFloat(f.goals_score), competency_score: parseFloat(f.competency_score), overall_score: parseFloat(f.overall_score) }); onSaved(); } catch (e) { setErr(e?.response?.data?.detail || "Failed"); } };
  return (
    <Modal title="Score employee" onClose={onClose} testid="appraisal-modal" wide>
      <form onSubmit={save} className="grid grid-cols-2 gap-4" data-testid="appraisal-form">
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Cycle</span>
          <select required value={f.cycle_id} onChange={(e) => setF({ ...f, cycle_id: e.target.value })} data-testid="ap-cycle" className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            {cycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Employee</span>
          <select required value={f.employee_id} onChange={(e) => setF({ ...f, employee_id: e.target.value })} data-testid="ap-emp" className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            <option value="">— select —</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.employee_no} · {e.name}</option>)}
          </select>
        </label>
        <Field label="Goals score (0-5)" type="number" required value={f.goals_score} onChange={(v) => setF({ ...f, goals_score: v })} testid="ap-goals" />
        <Field label="Competency score (0-5)" type="number" required value={f.competency_score} onChange={(v) => setF({ ...f, competency_score: v })} testid="ap-comp" />
        <Field label="Overall score (0-5)" type="number" required value={f.overall_score} onChange={(v) => setF({ ...f, overall_score: v })} testid="ap-overall" />
        <div></div>
        <div className="col-span-2"><Field label="Strengths" value={f.strengths} onChange={(v) => setF({ ...f, strengths: v })} testid="ap-strengths" /></div>
        <div className="col-span-2"><Field label="Growth areas" value={f.growth_areas} onChange={(v) => setF({ ...f, growth_areas: v })} testid="ap-growth" /></div>
        {err && <div className="col-span-2 text-sm text-[#D90429]">{err}</div>}
        <div className="col-span-2 flex justify-end gap-2"><button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button><button type="submit" data-testid="ap-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Save</button></div>
      </form>
    </Modal>
  );
}

/* =============================================================
 *  RECRUITMENT ATS
 * ============================================================= */
function RecruitmentTab() {
  const [jobs, setJobs] = useState([]);
  const [cands, setCands] = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [newJob, setNewJob] = useState(false);
  const [newCand, setNewCand] = useState(false);
  const load = () => Promise.all([
    api.get("/hr/jobs").then(({ data }) => setJobs(data)),
    api.get("/hr/candidates").then(({ data }) => setCands(data)),
    api.get("/hr/candidates/pipeline").then(({ data }) => setPipeline(data)),
  ]);
  useEffect(() => { load(); }, []);
  const advance = async (id, stage) => { await api.post(`/hr/candidates/${id}/stage`, { stage }); load(); };
  const STAGES = ["applied", "screening", "interview", "offer", "hired", "rejected"];
  return (
    <div className="mt-6">
      <div className="flex justify-end gap-2 mb-4">
        <button onClick={() => setNewJob(true)} data-testid="new-job-btn" className="inline-flex items-center gap-2 border border-[#0A0A0A] px-4 py-2.5 text-sm"><Briefcase size={14}/> New job</button>
        <button onClick={() => setNewCand(true)} disabled={jobs.length === 0} data-testid="new-candidate-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm disabled:opacity-50"><UserPlus size={14}/> New candidate</button>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-4" data-testid="pipeline-kpis">
        {STAGES.map((s) => (
          <div key={s} className="border border-[#DEE2E6] bg-white p-3" data-testid={`pipeline-${s}`}>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#495057]">{s}</div>
            <div className="text-xl font-bold tabular-nums">{pipeline[s] || 0}</div>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-[#0A0A0A] bg-white" data-testid="jobs-table">
          <div className="bg-[#F8F9FA] px-4 py-2 text-xs uppercase tracking-[0.15em] text-[#495057] border-b border-[#DEE2E6]">Open job postings</div>
          {jobs.map((j) => (
            <div key={j.id} className="px-4 py-3 border-b border-[#DEE2E6] last:border-0 text-sm" data-testid={`job-${j.id}`}>
              <div className="font-medium">{j.title}</div>
              <div className="text-xs text-[#495057]">{j.number} · {j.department} · {j.location} · {j.employment_type.replace("_", " ")}</div>
            </div>
          ))}
          {jobs.length === 0 && <div className="p-8 text-center text-[#495057]">No job postings.</div>}
        </div>
        <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="candidates-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
              <tr><Th>Candidate</Th><Th>Job</Th><Th>Stage</Th><Th>Move</Th></tr>
            </thead>
            <tbody>
              {cands.map((c, i) => (
                <tr key={c.id} className="border-b border-[#DEE2E6]" data-testid={`candidate-row-${i}`}>
                  <Td><div className="font-medium">{c.name}</div><div className="text-xs text-[#495057]">{c.email}</div></Td>
                  <Td className="text-xs text-[#495057]">{c.job_title}</Td>
                  <Td><span className="text-[10px] uppercase tracking-[0.15em] border border-[#0A0A0A] px-2 py-0.5">{c.stage}</span></Td>
                  <Td>
                    <select onChange={(e) => advance(c.id, e.target.value)} value={c.stage} data-testid={`cand-stage-${i}`} className="border border-[#DEE2E6] px-2 py-1 text-xs">
                      {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Td>
                </tr>
              ))}
              {cands.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[#495057]">No candidates.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {newJob && <JobForm onClose={() => setNewJob(false)} onSaved={() => { setNewJob(false); load(); }} />}
      {newCand && <CandForm jobs={jobs} onClose={() => setNewCand(false)} onSaved={() => { setNewCand(false); load(); }} />}
    </div>
  );
}

function JobForm({ onClose, onSaved }) {
  const [f, setF] = useState({ title: "", department: "General", location: "Accra", description: "", employment_type: "full_time" });
  const [err, setErr] = useState("");
  const save = async (e) => { e.preventDefault(); try { await api.post("/hr/jobs", f); onSaved(); } catch (e) { setErr(e?.response?.data?.detail || "Failed"); } };
  return (
    <Modal title="New job posting" onClose={onClose} testid="job-modal">
      <form onSubmit={save} className="space-y-4" data-testid="job-form">
        <Field label="Title" required value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="job-title" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Department" value={f.department} onChange={(v) => setF({ ...f, department: v })} testid="job-dept" />
          <Field label="Location" value={f.location} onChange={(v) => setF({ ...f, location: v })} testid="job-loc" />
        </div>
        <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Type</span>
          <select value={f.employment_type} onChange={(e) => setF({ ...f, employment_type: e.target.value })} data-testid="job-type" className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            {["full_time", "part_time", "contract", "intern"].map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
          </select>
        </label>
        <Field label="Description" value={f.description} onChange={(v) => setF({ ...f, description: v })} testid="job-desc" />
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button><button type="submit" data-testid="job-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Save</button></div>
      </form>
    </Modal>
  );
}

function CandForm({ jobs, onClose, onSaved }) {
  const [f, setF] = useState({ job_id: jobs[0]?.id || "", name: "", email: "", phone: "", source: "direct", notes: "" });
  const [err, setErr] = useState("");
  const save = async (e) => { e.preventDefault(); try { await api.post("/hr/candidates", f); onSaved(); } catch (e) { setErr(e?.response?.data?.detail || "Failed"); } };
  return (
    <Modal title="New candidate" onClose={onClose} testid="cand-modal">
      <form onSubmit={save} className="space-y-4" data-testid="cand-form">
        <label className="block"><span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Job</span>
          <select required value={f.job_id} onChange={(e) => setF({ ...f, job_id: e.target.value })} data-testid="cand-job" className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.number} · {j.title}</option>)}
          </select>
        </label>
        <Field label="Name" required value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="cand-name" />
        <Field label="Email" type="email" required value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="cand-email" />
        <Field label="Phone" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="cand-phone" />
        <Field label="Source" value={f.source} onChange={(v) => setF({ ...f, source: v })} testid="cand-source" />
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button><button type="submit" data-testid="cand-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Save</button></div>
      </form>
    </Modal>
  );
}
